import {
  Button,
  Icon,
  InformationCard,
  Text,
  useIsMobile
} from '@yes.technology/react-toolkit'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import styled from 'styled-components'
import {
  AreaSelector,
  IArea,
  IAreaRendererProps
} from '@bmunozg/react-image-area'
import MarkingCategoryModal, { MarkingCategory } from './MarkingCategoryModal'
import MarkList from './MarkList'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'
import { IRectangle } from '@bmunozg/react-image-area/dist/src/types'

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;

  .media-wrapper {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
  }
`

const MarkBadge = styled.div`
  position: absolute;
  top: -4px;
  left: 4px;
  background: #1976d2;
  color: white;
  font-size: 12px;
  padding: 2px 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 12px;
  align-content: center;
`

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`

const ModeToggle = styled.div`
  display: flex;
  margin: 0.5rem 0;
  gap: 0.5rem;
`

const RemoveButton = styled(Button)`
  flex: 0;
`

export type PhotoMark = {
  x: number
  y: number
  width: number
  height: number
  category: MarkingCategory
}

type PhotoMarkingModalProps = {
  isOpen: boolean
  image: string
  categoriesFilteraction: string
  parentPath: string[]
  initialMarks?: PhotoMark[]
  onSave: (marks: PhotoMark[]) => void
  onCancel: () => void
  isFullscreen?: boolean
  categorySelectionModal?: { des: string; complement?: string }
}

const PhotoMarkingModal = ({
  isOpen,
  image,
  categoriesFilteraction,
  parentPath,
  initialMarks = [],
  onSave,
  onCancel,
  isFullscreen,
  categorySelectionModal
}: PhotoMarkingModalProps) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const [markedAreas, setMarkedAreas] = useState<IArea[]>([])
  const [confirmedMarks, setConfirmedMarks] =
    useState<PhotoMark[]>(initialMarks)
  const [pendingArea, setPendingArea] = useState<IArea | null>(null)
  const [selectedCategory, setSelectedCategory] =
    useState<MarkingCategory | null>(null)
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [mode, setMode] = useState<'draw' | 'move'>('draw')
  const [transformState, setTransformState] = useState({
    scale: 1,
    positionX: 0,
    positionY: 0
  })
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 })

  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const handleResize = () => {
      const img = imageRef.current
      if (img) setImgSize({ w: img.clientWidth, h: img.clientHeight })
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setConfirmedMarks(initialMarks)
  }, [initialMarks])

  useEffect(() => {
    if (!isOpen) {
      setConfirmedMarks([])
    }
  }, [isOpen])

  useEffect(() => {
    if (pendingArea || imgSize.w === 0) return
    const { scale, positionX, positionY } = transformState
    setMarkedAreas(
      confirmedMarks.map((mark) => ({
        x: (mark.x / 100) * imgSize.w * scale + positionX,
        y: (mark.y / 100) * imgSize.h * scale + positionY,
        width: (mark.width / 100) * imgSize.w * scale,
        height: (mark.height / 100) * imgSize.h * scale,
        unit: 'px',
        isChanging: false
      }))
    )
  }, [confirmedMarks, transformState, pendingArea, imgSize])

  const openCategoryModal = () => {
    setSelectedCategory(null)
    setIsCategoryModalOpen(true)
  }

  const handleAreasChange = (areas: IArea[]) => {
    setMarkedAreas(areas)
    const changedArea = areas.find((area) => area.isChanging)

    if (changedArea) {
      setPendingArea(changedArea)
      return
    }

    if (areas.length > confirmedMarks.length) {
      openCategoryModal()
    }

    if (areas.length === confirmedMarks.length && !changedArea) {
      const updatedMarks = areas.map((area, index) => {
        return {
          ...confirmedMarks[index],
          ...calculateMarkWithScale(area)
        }
      })
      setConfirmedMarks(updatedMarks)
    }
  }

  const calculateMarkWithScale = (area: IArea): IRectangle => {
    const { scale, positionX, positionY } = transformState
    const w = imgSize.w || 1
    const h = imgSize.h || 1
    const imageX = (area.x - positionX) / scale
    const imageY = (area.y - positionY) / scale

    return {
      x: (imageX / w) * 100,
      y: (imageY / h) * 100,
      width: (area.width / scale / w) * 100,
      height: (area.height / scale / h) * 100
    }
  }

  const handleConfirmCategory = () => {
    if (!pendingArea || !selectedCategory) return

    const savedMark: PhotoMark = {
      ...calculateMarkWithScale(pendingArea),
      category: selectedCategory
    }

    setConfirmedMarks((prev) => [...prev, savedMark])
    setPendingArea(null)
    setSelectedCategory(null)
    setIsCategoryModalOpen(false)
  }

  const handleCancelCategory = () => {
    setPendingArea(null)
    setSelectedCategory(null)
    setIsCategoryModalOpen(false)
  }

  const removeAllMarks = () => {
    setConfirmedMarks([])
  }

  const removeMarkAt = (index: number) => {
    setConfirmedMarks((prev) => prev.filter((_, i) => i !== index))
  }

  const customAreaRenderer = (areaProps: IAreaRendererProps) => (
    <div
      key={areaProps.areaNumber}
      style={{
        border: '2px solid #1976d2',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box'
      }}
    >
      <MarkBadge>#{areaProps.areaNumber}</MarkBadge>
    </div>
  )

  const handleSave = () => {
    onSave(confirmedMarks)
  }

  return (
    <>
      <InformationCard
        isOpen={isOpen}
        title={t('activity-document-photo.mark-photo.modal-title')}
        close={onCancel}
        style={{
          width: '1200px',
          maxWidth: isFullscreen || isMobile ? '100%' : '95%',
          maxHeight: isFullscreen || isMobile ? '100%' : '95%',
          overflowY: 'auto',
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          margin: 0,
          zIndex: 1050,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Text variant='label-02'>
          {t('activity-document-photo.mark-photo.modal-instruction')}
        </Text>

        <ModeToggle>
          <Button
            variant={mode === 'draw' ? 'primary' : 'secondary'}
            onClick={() => setMode('draw')}
          >
            {t('activity-document-photo.mark-photo.mode-draw')}
          </Button>
          <Button
            variant={mode === 'move' ? 'primary' : 'secondary'}
            onClick={() => setMode('move')}
          >
            {t('activity-document-photo.mark-photo.mode-move')}
          </Button>

          <RemoveButton
            impactClass='navigation'
            variant='secondary'
            onClick={() => setIsDeleteConfirmOpen(true)}
            title={t('activity-document-photo.mark-photo.remove-mark')}
            heightClass='semiSmall'
            style={{
              minWidth: '40px',
              marginLeft: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Icon iconName='Cleaner' icSize='small' />
          </RemoveButton>
        </ModeToggle>

        <TransformWrapper
          panning={{ disabled: mode !== 'move' }}
          pinch={{ disabled: mode !== 'move' }}
          onTransform={(_ref, state) =>
            setTransformState({
              scale: state.scale,
              positionX: state.positionX,
              positionY: state.positionY
            })
          }
          disabled={mode === 'draw'}
        >
          <ImageWrapper>
            <AreaSelector
              areas={markedAreas}
              onChange={handleAreasChange}
              customAreaRenderer={customAreaRenderer}
              wrapperStyle={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
              mediaWrapperClassName='media-wrapper'
            >
              <TransformComponent
                wrapperStyle={{ flex: '1' }}
                contentStyle={{
                  height: '100%',
                  cursor: mode === 'draw' ? 'inherit' : 'move'
                }}
                contentProps={{
                  onPointerDown: (e) => {
                    mode === 'move' && e.stopPropagation()
                  }
                }}
              >
                <img
                  ref={imageRef}
                  src={image}
                  alt='Photo to mark'
                  onLoad={() => {
                    const img = imageRef.current
                    if (img)
                      setImgSize({
                        w: img.clientWidth,
                        h: img.clientHeight
                      })
                  }}
                />
              </TransformComponent>
            </AreaSelector>
          </ImageWrapper>
        </TransformWrapper>

        <MarkList marks={confirmedMarks} onDelete={removeMarkAt} />

        <ButtonRow>
          <Button onClick={handleSave}>
            {t('activity-document-photo.mark-photo.save')}
          </Button>
          <Button variant='secondary' onClick={onCancel}>
            {t('activity-document-photo.mark-photo.cancel')}
          </Button>
        </ButtonRow>

        <InformationCard
          isOpen={isDeleteConfirmOpen}
          title={t('activity-document-photo.mark-photo.delete-all-marks-title')}
          close={() => setIsDeleteConfirmOpen(false)}
          style={{
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            margin: 0,
            zIndex: 1100
          }}
        >
          <Text variant='body-mobile-04'>
            {t('activity-document-photo.mark-photo.delete-all-marks-body')}
          </Text>
          <ButtonRow>
            <Button
              onClick={() => {
                removeAllMarks()
                setIsDeleteConfirmOpen(false)
              }}
            >
              {t('activity-document-photo.mark-photo.delete-all-marks-confirm')}
            </Button>
            <Button
              variant='secondary'
              onClick={() => setIsDeleteConfirmOpen(false)}
            >
              {t('activity-document-photo.mark-photo.cancel')}
            </Button>
          </ButtonRow>
        </InformationCard>

        <MarkingCategoryModal
          isOpen={isCategoryModalOpen}
          categoriesFilteraction={categoriesFilteraction}
          parentPath={parentPath}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
          onConfirm={handleConfirmCategory}
          onCancel={handleCancelCategory}
          categorySelectionModal={categorySelectionModal}
        />
      </InformationCard>
    </>
  )
}

export default PhotoMarkingModal
